import { ContactEntity } from '../contact/contact.options';
import { JobPolicyThresholds } from '../policies/job.policy.thresholds';
import { PriorityTiny } from '../job/priority.tiny';
import { SiteTiny } from '../shared/site';
import { AuditDetails, Entity, SearchableEntity } from '../entity';
import { CreateWorkOrderRequest } from '../job/workorder';
import { DailyPhaseName } from '../tasks/task.enum';
import { WorkOrderStatus } from '../job/workorder.status';
import { JobMetadata } from './job.pro';
import { WorkOrderProcessingDataRequest } from '@jms/model/requests/job/job.processing.request';

export interface StageMetadata extends Entity, AuditDetails {

    stageNumber: string;
    version?: number;

    status: WorkOrderStatus;
    priority?: PriorityTiny;
    type?: Entity;

    additionalNote?: string;
    description: string;

    estimatedDuration?: number;

    startDate?: Date;
    dueDate?: Date;

    finalisedDate?: Date;
    finalisedBy?: Entity;
    finalisedNote?: string;

    completedDate?: Date;
    completedBy?: Entity;
    completedSignature?: string;
    completedSignatureName?: string;
    completedNote?: string;

    billed?: boolean;
    bills?: string[];

    contractor: ContactEntity;
    contractorResources?: ContactEntity[];
    resources?: ContactEntity[];

    priceBook?: Entity;
    billingCompany?: Entity;

    buyPrice?: number;
    sellPrice?: number;

    alternateSite?: SiteTiny;

    theme?: Entity
    workCategory?: Entity;
    tags?: Entity[];
    systemTags?: Entity[];
    schedulingTags?: Entity[];

    thresholds?: JobPolicyThresholds;

    customFields?: Record<string, any>;

}

export interface Stage extends StageMetadata, SearchableEntity {

    job: JobMetadata;

    integrationId?: string;
    integrationFields?: Record<string, any>;

    manuallyUpdated?: boolean;
    previousStatus?: WorkOrderStatus;
    notifyTechnician?: boolean;
    hasAppointment?: boolean;
    defectCount?: number;

    /** This token is set when a job is imported in an upstream tenant .. ie  Asset sends a job to Alectrix via email. The job is then imported via the importer. */
    claimToken?: string;
}

export enum StageCommandIds {
    ValidateProcessing = 'ValidateProcessing',
    CreateStage = 'CreateStage',
    /** Field-app technician status transition (Enroute / Onsite / Checked Out). */
    ProcessStage = 'ProcessStage',
    /**
     * Field-app daily task instances for a phase, used by the Stop sheets.
     *
     * NOT a pure read: the underlying logic generates and persists today's
     * instances from the phase-tagged parents, which is why this is a command
     * rather than a `getItems` on a Task field strategy.
     */
    GetDailyPhaseTasks = 'GetDailyPhaseTasks',
    /**
     * Field-app completion gate.
     *
     * Returns the INCOMPLETE mandatory tasks, so a non-empty result means the
     * gate is NOT satisfied. Reading it as a boolean inverts the gate and lets
     * every job complete.
     */
    GetIncompleteMandatoryTasks = 'GetIncompleteMandatoryTasks',
    //Raises the user's intent to submit supplementary works for a stage. For NEW lines core does not
    //decide what gets submitted: the integration reads the stage's pending items when the outbound
    //event is processed and may legitimately do nothing. Changes to lines the customer's system
    //already holds are the exception — they exist nowhere but the request, so the command carries them.
    SubmitSupplementaryWorks = 'SubmitSupplementaryWorks',
    //TEMPORARY (BGIS SIT, Sep 2026). Same intent as SubmitSupplementaryWorks but the user is asserting
    //the items need no authority, so the integration adds them to the upstream job directly. Exists
    //because the value-threshold rule that should make this choice is not yet agreed; delete this,
    //its command, event and quick action when that rule ships.
    AddItemsWithoutAuthority = 'AddItemsWithoutAuthority',
    //Asks the customer's system to delete selected lines it already holds (WBS-12839). Those lines are
    //client-locked, so this is the only way a user can remove one; the integration cancels them here
    //once the customer accepts.
    RequestItemDeletion = 'RequestItemDeletion',
    //Which pending-item status the tenant's integration submits as new supplementary lines (WBS-13013),
    //so the request dialog can preview them. The integration owns that setting and answers for it;
    //core only relays the question.
    GetSupplementaryWorksAwaitingStatus = 'GetSupplementaryWorksAwaitingStatus',
    //One supplementary works request for every open stage of a job (WBS-13021): the customer actions
    //requests one at a time, so a large job goes as one request, not one per stage. Each change to an
    //existing line names its item; the server finds the stage that holds it.
    SubmitJobSupplementaryWorks = 'SubmitJobSupplementaryWorks',
}

//One change a user asked for on a line the customer's system already holds (WBS-10994), as the
//browser sends it: WHICH line (by our item id) and WHAT the user wants. The line itself is never
//edited locally — it is readonly by design — so this is the only record of the request until the
//customer's decision arrives. `cancel` wins: a cancelled line carries no quantity or location change.
//`reason` is mandatory on every change by the HomesNSW contract.
export interface StageSupplementaryExistingLineUpdateRequest {
    itemId: string;
    quantity?: number;
    location?: string;
    cancel?: boolean;
    reason: string;
}

//The same change once the server has resolved the item on the stage it loaded: the customer's line
//number comes from OUR record of the line, never from the browser. This is what travels on the
//outbound event.
export interface StageSupplementaryExistingLineUpdate extends StageSupplementaryExistingLineUpdateRequest {
    integrationId: string;
}

//An optional request for more time, raised alongside a supplementary request (WBS-11542). The two
//fields travel as a pair or not at all: the customer's system makes each mandatory once the other is
//supplied, so "days with no reason" is not a request anyone can act on and is refused before sending.
//Which integration can carry one, and what it does with it, is the integration's business — core only
//carries the user's intent, the same way it does for the items themselves.
export interface StageSupplementaryExtensionRequest {
    days: number;
    reason: string;
}

//One line the user asked to have deleted (WBS-12839), once the server has resolved it on the stage it
//loaded: the customer's line number comes from OUR record of the line, never from the browser. This
//is what travels on the outbound event.
export interface StageItemDeletion {
    itemId: string;
    integrationId: string;
}

//The integration's answer to "which pending items go out as new supplementary lines" (WBS-13013).
//null when the integration has no such status configured, in which case it sends no new lines.
export interface StageSupplementaryAwaitingStatus {
    awaitingSubmissionStatus: string | null;
}

export type StageCommandRequest =
    {
        commandId: StageCommandIds.ValidateProcessing,
        data: {
            jobId: string;
            workOrderId: string;
            systemStatusName: string;
            action?: 'delete' | 'update';
        }
    }
    | {
        commandId: StageCommandIds.CreateStage,
        data: CreateWorkOrderRequest;
    }
    | {
        commandId: StageCommandIds.ProcessStage,
        // Inline type import: `entities/ -> requests/` is a type-only cycle, and an
        // inline reference cannot be stripped as an unused import.
        data: WorkOrderProcessingDataRequest;
    }
    | {
        commandId: StageCommandIds.GetDailyPhaseTasks,
        data: {
            workOrderId: string;
            phase: DailyPhaseName;
        }
    }
    | {
        commandId: StageCommandIds.GetIncompleteMandatoryTasks,
        data: {
            workOrderId: string;
        }
    }
    | {
        commandId: StageCommandIds.SubmitSupplementaryWorks,
        data: {
            jobId: string;
            workOrderId: string;
            existingLineUpdates?: StageSupplementaryExistingLineUpdateRequest[];
            extensionOfTime?: StageSupplementaryExtensionRequest;
        }
    }
    | {
        commandId: StageCommandIds.AddItemsWithoutAuthority,
        data: {
            jobId: string;
            workOrderId: string;
        }
    }
    | {
        commandId: StageCommandIds.RequestItemDeletion,
        data: {
            jobId: string;
            workOrderId: string;
            itemIds: string[];
        }
    }
    | {
        commandId: StageCommandIds.GetSupplementaryWorksAwaitingStatus,
        data: Record<string, never>;
    }
    | {
        commandId: StageCommandIds.SubmitJobSupplementaryWorks,
        data: {
            jobId: string;
            existingLineUpdates?: StageSupplementaryExistingLineUpdateRequest[];
        }
    };